import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { GroupOutlined, ErrorOutline } from '@material-ui/icons';
import {
  ceil, compose, debounce,
  find, get, groupBy,
  isEmpty, last, map,
  sumBy, sortBy, split,
  toNumber, toString, uniqBy,
} from 'lodash/fp';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Snackbar, Fade, Button as MuiButton } from '@material-ui/core';
import {
  Grid,
  Heading,
  Pagination,
  RowsPerPageFilter,
  SearchField,
  Spinner,
  TableFallbackMessage,
  Dialog,
  DialogActions,
  Button,
  DialogContent,
} from '../../common/components';
import {
  ContactAddLink,
  ContactDetailDialog,
  ContactEditDialog,
  ContactExportButton,
  ContactExportDialog,
  ContactTable,
  GroupAddDialog,
  GroupFilter,
  GroupSettingsDialog,
} from '../components';
import * as actions from '../actions';
import messages from '../messages';
import { XLSXExporter } from '../modules';

const EXPORT_EXCEL_PER_PAGE = 100;

const rowsPerPageOptions = map(rowCount => ({
  id: toString(rowCount),
  value: toString(rowCount),
  label: <FormattedMessage {...messages.rowsPerPageFilterLabel} values={{ num: rowCount }} />,
}), [10, 15, 20]);

const S = {
  Count: styled.data`
    margin-left: 8px;
    padding: 5px 0;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 0.75rem;
    line-height: 1.5;
  `,
  CenteredGrid: styled(Grid)`
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  ContactAddLink: styled(ContactAddLink)`
    margin-left: 8px;
  `,
  Option: styled.div`
    display: flex;
    align-items: center;
  `,
  GroupOutlined: styled(GroupOutlined)`
    margin-right: 8px;
    color: ${({ theme }) => theme.palette.grey[500]};
    font-size: 1.25rem;
  `,
  Message: styled.p`
    display: flex;
    align-items: center;
  `,
  ErrorOutline: styled(ErrorOutline)`
    margin-right: 0.5rem;
  `,
  ErrorDialogContent: styled(DialogContent)`
    margin-bottom: 40px;
  `,
  RetryButton: styled(Button)`
    border-color: #e15863;
    color: #e15863;
  `,
  SnackbarRetryButton: styled(MuiButton)`
    color: ${({ theme }) => theme.palette.error.main};
  `,
};

const Contacts = ({ intl, location, match }) => {
  const [searchWord, setSearchWord] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState('10');
  const [pageNumber, setPageNumber] = useState(1);
  const [group, setGroup] = useState({ type: 'allGroup', value: 'allGroup', computed: '' });
  const [open, setOpen] = useState({
    contactDetail: false,
    contactEdit: false,
    contactExport: false,
    groupFilter: false,
    groupSettings: false,
    groupAdd: false,
    deleteGroupCheck: false,
  });
  const [all, setAll] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogText, setErrorDialogText] = useState('');
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorSnackbarText, setErrorSnackbarText] = useState('');
  const [visibleSnackbarRetryButton, setVisibleSnackbarRetryButton] = useState(false);
  const [errorMsgSnackbarOpen, setErrorMsgSnackbarOpen] = useState(false);
  const [errorMsgSnackbarText, setErrorMsgSnackbarText] = useState('');

  const contact = useSelector(state => state.contacts.contact);
  const contacts = useSelector(state => state.contacts.contacts);
  const contactsCount = useSelector(state => state.contacts.contactsCount);
  const allContacts = useSelector(state => state.contacts.allContacts);
  const groups = useSelector(state => state.contacts.groups);
  const creationTask = useSelector(state => state.contacts.creationTask);
  const updateTask = useSelector(state => state.contacts.updateTask);
  const updated = useSelector(state => state.contacts.updated);
  const contactsDeleted = useSelector(state => state.contacts.contactsDeleted);
  const failedContactsDeletedId = useSelector(state => state.contacts.failedContactsDeletedId);
  const adminRelationships = useSelector(state => state.admin.adminRelationships);

  const dispatch = useDispatch();

  const pageCount = useMemo(
    () => (ceil(get('total', contacts.data) / rowsPerPage) || 1),
    [contacts.data, rowsPerPage],
  );

  const options = useMemo(() => [
    {
      id: 'allGroup', type: 'allGroup', value: 'allGroup', label: '전체 보기',
    },
    ...compose(
      map.convert({ cap: false })((gradeGroup, key) => ({
        id: `gradeGroup_${key}`,
        type: 'gradeGroup',
        value: `gradeGroup_${key}`,
        label: (
          <S.Option>
            <S.GroupOutlined />
            <span>{`${key}학년 (${sumBy(g => g.contacts_count, gradeGroup)}명)`}</span>
          </S.Option>
        ),
        suboptions: [
          {
            id: `gradeGroup_${key}`, type: 'gradeGroup', label: '전체', value: key,
          },
          ...map(clazz => ({
            id: clazz.id, type: 'clazzGroup', value: clazz.id, label: `${clazz.class_name}반 (${clazz.contacts_count}명)`,
          }), gradeGroup),
        ],
      })),
      groupBy(clazzGroup => clazzGroup.grade),
      sortBy(['grade', 'class_name']),
      get('clazzGroups'),
    )(groups.data),
    ...compose(
      map(customGroup => ({
        id: `customGroup_${customGroup.id}`,
        type: 'customGroup',
        value: `customGroup_${customGroup.id}`,
        label: `${customGroup.name || '미지정'} (${customGroup.contacts_count}명)`,
      })),
      sortBy(['is_unspecified_group']),
      get('customGroups'),
    )(groups.data),
  ], [groups.data]);

  const labels = useMemo(() => [
    { id: 'allGroup', value: 'allGroup', label: '전체 보기' },
    ...compose(
      map(clazzGroup => ({
        id: `gradeGroup_${clazzGroup.grade}`,
        value: `gradeGroup_${clazzGroup.grade}`,
        label: `${clazzGroup.grade}학년 전체`,
      })),
      uniqBy(clazzGroup => clazzGroup.grade),
      get('clazzGroups'),
    )(groups.data),
    ...map(clazzGroup => ({
      id: `clazzGroup_${clazzGroup.id}`,
      value: `clazzGroup_${clazzGroup.id}`,
      label: `${clazzGroup.grade}학년 ${clazzGroup.class_name}반`,
    }), get('clazzGroups', groups.data)),
    ...map(customGroup => ({
      id: `customGroup_${customGroup.id}`,
      value: `customGroup_${customGroup.id}`,
      label: customGroup.name || '미지정',
    }), get('customGroups', groups.data)),
  ], [groups.data]);

  const schoolName = compose(
    get('school.name'),
    find(adminRelationship => get('school.sid', adminRelationship) === match.params.sid),
  )(adminRelationships.data);

  const fileName = XLSXExporter.getFileName({ schoolName, groups, selectedGroup: group });

  const fetchContactsCount = useCallback((...args) => dispatch(actions.fetchContactsCountRequest(...args)), [dispatch]);
  const selectAllContacts = useCallback(() => setAll(true), []);
  const deselectAllContacts = useCallback(() => setAll(false), []);
  const fetchContact = useCallback((...args) => dispatch(actions.fetchContactRequest(...args)), [dispatch]);
  const fetchContacts = useCallback((...args) => dispatch(actions.fetchContactsRequest(...args)), [dispatch]);
  const fetchContactsByName = useCallback(debounce(500, (...args) => dispatch(actions.fetchContactsRequest(...args))), [dispatch]);

  const fetchContactsAll = useCallback(() => {
    const scopeValue = last(split('_', group.value));

    switch (group.type) {
      case 'clazzGroup':
        dispatch(actions.fetchContactsAllByClazzGroupRequest({ gid: scopeValue, perPage: EXPORT_EXCEL_PER_PAGE }));
        break;
      case 'customGroup':
        dispatch(actions.fetchContactsAllByCustomGroupRequest({ gid: scopeValue, perPage: EXPORT_EXCEL_PER_PAGE }));
        break;
      case 'gradeGroup':
        dispatch(actions.fetchContactsAllBySchoolRequest({ sid: match.params.sid, grade: scopeValue, perPage: EXPORT_EXCEL_PER_PAGE }));
        break;
      default:
        dispatch(actions.fetchContactsAllBySchoolRequest({ sid: match.params.sid, perPage: EXPORT_EXCEL_PER_PAGE }));
    }
  }, [dispatch, match, group]);

  const fetchContactsByClazzGroup = useCallback((...args) => dispatch(actions.fetchContactsByClazzGroupRequest(...args)), [dispatch]);
  const fetchContactsByCustomGroup = useCallback((...args) => dispatch(actions.fetchContactsByCustomGroupRequest(...args)), [dispatch]);
  const fetchGroups = useCallback((...args) => dispatch(actions.fetchGroupsRequest(...args)), [dispatch]);
  const deleteContacts = useCallback((...args) => dispatch(actions.deleteContactsRequest(...args)), [dispatch]);
  const deleteContactsAll = useCallback((...args) => dispatch(actions.deleteContactsAllRequest(...args)), [dispatch]);
  const createClazzGroup = useCallback((...args) => dispatch(actions.createClazzGroupRequest(...args)), [dispatch]);
  const createCustomGroup = useCallback((...args) => dispatch(actions.createCustomGroupRequest(...args)), [dispatch]);
  const applyChangedGroups = useCallback((...args) => dispatch(actions.applyChangedGroupsRequest(...args)), [dispatch]);
  const updateContact = useCallback((...args) => dispatch(actions.updateContactRequest(...args)), [dispatch]);
  const exchangeGuardianContacts = useCallback((...args) => dispatch(actions.exchangeGuardianContactsRequest(...args)), [dispatch]);
  const initContact = useCallback((...args) => dispatch(actions.initContact(...args)), [dispatch]);
  const initAllContacts = useCallback((...args) => dispatch(actions.initAllContacts(...args)), [dispatch]);
  const initCreationTask = useCallback((...args) => dispatch(actions.initCreationTask(...args)), [dispatch]);
  const initUpdateTask = useCallback((...args) => dispatch(actions.initUpdateTask(...args)), [dispatch]);
  const initContactsDeleted = useCallback((...args) => dispatch(actions.initContactsDeleted(...args)), [dispatch]);
  const handleGroupFilterOpen = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, groupFilter: true })), []);
  const handleGroupFilterClose = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, groupFilter: false })), []);
  const handleGroupChange = useCallback((event) => {
    if (event.target.value === 'settings') {
      setOpen(prevOpen => ({ ...prevOpen, groupFilter: false, groupSettings: true }));
    } else {
      setOpen(prevOpen => ({ ...prevOpen, groupFilter: false }));
      setGroup({ type: event.currentTarget.dataset.type, value: event.target.value, computed: event.target.value });
    }
  }, []);
  const handleSubgroupChange = useCallback((event) => {
    event.stopPropagation();

    setOpen(prevOpen => ({ ...prevOpen, groupFilter: false }));
    setGroup({
      type: event.currentTarget.dataset.type,
      value: event.currentTarget.dataset.value,
      computed: event.currentTarget.dataset.value,
    });
  }, []);
  const handleSearchWordChange = useCallback((event) => {
    setSearchWord(event.target.value);

    // TODO: 검색어 길이가 2글자 이상일 경우에만 요청하도록 변경
    fetchContactsByName({
      query: event.target.value,
      sid: match.params.sid,
      perPage: rowsPerPage,
      pageNumber,
    });
  }, [fetchContactsByName, match.params.sid, rowsPerPage, pageNumber]);
  const handleSearchWordClear = useCallback(() => setSearchWord(''), []);
  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(event.target.value);

    setPageNumber(1);
  }, []);
  const handlePageNumberClick = useCallback((event) => {
    setPageNumber(toNumber(event.currentTarget.dataset.pageNumber));

    deselectAllContacts();
  }, [deselectAllContacts]);
  const handleContactDetailOpen = useCallback(cid => setOpen(prevOpen => ({ ...prevOpen, contactDetail: cid })), []);
  const handleContactDetailClose = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, contactDetail: false })), []);
  const handleContactEditOpen = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, contactEdit: true })), []);
  const handleContactEditClose = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, contactEdit: false })), []);
  const handleGroupSettingsClose = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, groupSettings: false })), []);
  const handleGroupAddOpen = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, groupAdd: true })), []);
  const handleGroupAddClose = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, groupAdd: false })), []);
  const handleContactDetailEnter = useCallback(() => fetchContact({ cid: open.contactDetail }), [fetchContact, open.contactDetail]);
  const handleContactExportOpen = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, contactExport: true })), []);
  const handleContactExportClose = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, contactExport: false })), []);
  const handleDeleteGroupCheckOpen = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, deleteGroupCheck: true })), []);
  const handleDeleteGroupCheckClose = useCallback(() => setOpen(prevOpen => ({ ...prevOpen, deleteGroupCheck: false })), []);
  const handleDelete = useCallback(({ cids }) => (all
    ? deleteContactsAll({ sid: match.params.sid })
    : deleteContacts({ cids })),
  [deleteContactsAll, deleteContacts, match.params.sid, all]);

  const requestFetchContacts = useCallback(() => {
    if (group.type === 'allGroup') {
      fetchContacts({ sid: match.params.sid, perPage: rowsPerPage, pageNumber });
    } else if (group.type === 'gradeGroup') {
      fetchContacts({
        sid: match.params.sid,
        grade: last(split('_', group.value)),
        pageNumber,
        perPage: rowsPerPage,
      });
    } else if (group.type === 'clazzGroup') {
      fetchContactsByClazzGroup({ gid: last(split('_', group.value)), pageNumber, perPage: rowsPerPage });
    } else {
      fetchContactsByCustomGroup({ gid: last(split('_', group.value)), pageNumber, perPage: rowsPerPage });
    }
  }, [
    group,
    fetchContacts,
    fetchContactsByClazzGroup,
    fetchContactsByCustomGroup,
    match.params.sid,
    pageNumber,
    rowsPerPage,
  ]);

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);

    dispatch(actions.clearContactsError());
    dispatch(actions.clearContactsCountError());
    dispatch(actions.clearAllContactsError());
  };

  const handleRetry = () => {
    handleCloseErrorDialog();

    if (contacts.error) {
      requestFetchContacts();
    }

    if (contactsCount.error) {
      fetchContactsCount({ sid: match.params.sid });
    }

    if (allContacts.error) {
      fetchContactsAll();
    }
  };

  const handleCloseErrorSnackbar = () => {
    setErrorSnackbarOpen(false);

    dispatch(actions.clearGroupsError());
    dispatch(actions.clearContactsError());
    dispatch(actions.clearContactsDeletedError());
    dispatch(actions.clearFailedContactsDeletedId());
  };

  const handleSnackbarRetry = () => {
    if (contacts.error && searchWord) {
      fetchContacts({
        query: searchWord,
        sid: match.params.sid,
        perPage: rowsPerPage,
        pageNumber,
      });
    }

    if ((all && contactsDeleted.error)
          || failedContactsDeletedId.length > 0) {
      handleDelete({ cids: failedContactsDeletedId });
    }

    handleCloseErrorSnackbar();
  };

  const handleCloseErrorMsgSnackbar = () => setErrorMsgSnackbarOpen(false);

  const handleErrorMsgSnackbarExited = () => setErrorMsgSnackbarText('');

  useEffect(() => {
    requestFetchContacts();
  }, [
    requestFetchContacts,
  ]);

  useEffect(() => {
    fetchGroups({ sid: match.params.sid });
  }, [fetchGroups, match.params.sid]);

  useEffect(() => {
    if ((pageNumber > pageCount) && pageCount) {
      setPageNumber(toString(pageCount));
    }
  }, [pageCount, pageNumber]);

  useEffect(() => {
    if (allContacts.data) {
      dispatch(actions.exportContacts({ data: allContacts.data, fileName }));

      initAllContacts();
    }
  }, [allContacts.data, dispatch, fileName, initAllContacts]);

  useEffect(() => {
    if (updated.data.groups) {
      handleGroupSettingsClose();

      dispatch(actions.initUpdated());
    }
  }, [updated.data.groups, handleGroupSettingsClose, dispatch]);

  useEffect(() => {
    fetchContactsCount({ sid: match.params.sid });
  }, [fetchContactsCount, match.params.sid]);

  useEffect(() => {
    if ((contacts.error && !searchWord) || contactsCount.error) {
      setErrorDialogText('목록을 불러오는데 실패하였습니다. 다시 시도하시겠습니까?');
      setErrorDialogOpen(true);
    } else if (allContacts.error) {
      setErrorDialogText('주소록 내보내기에 실패하였습니다. 다시 시도하시겠습니까?');
      setErrorDialogOpen(true);
    }
  }, [
    contacts.error,
    contactsCount.error,
    allContacts.error,
    searchWord,
  ]);

  useEffect(() => {
    if (groups.error) {
      setErrorSnackbarText('그룹 정보를 불러오는데 실패하였습니다.');

      setVisibleSnackbarRetryButton(false);
      setErrorSnackbarOpen(true);
    } else if (contacts.error && searchWord) {
      setErrorSnackbarText('목록을 불러오는데 실패하였습니다. 다시 시도해주세요.');

      setVisibleSnackbarRetryButton(true);
      setErrorSnackbarOpen(true);
    } else if ((all && contactsDeleted.error)
                  || failedContactsDeletedId.length > 0) {
      setErrorSnackbarText('삭제에 실패하였습니다. 다시 시도해주세요.');

      setVisibleSnackbarRetryButton(true);
      setErrorSnackbarOpen(true);
    }
  }, [
    groups.error,
    contacts.error,
    searchWord,
    all,
    contactsDeleted.error,
    failedContactsDeletedId,
  ]);

  useEffect(() => {
    if (contact.error) {
      setErrorMsgSnackbarOpen(true);
      setErrorMsgSnackbarText('연락처를 불러오는데 실패하였습니다.');
      dispatch(actions.initContact());
    }
  }, [dispatch, contact.error]);

  useEffect(() => {
    if (updateTask.error) {
      setErrorMsgSnackbarOpen(true);
      setErrorMsgSnackbarText('저장에 실패하였습니다.');
      dispatch(actions.initUpdateTask());
    }
  }, [dispatch, updateTask.error]);

  useEffect(() => {
    if (contactsDeleted.data) {
      if (pageNumber === 1) {
        if (group.type === 'allGroup') {
          fetchContacts({ sid: match.params.sid, perPage: rowsPerPage, pageNumber });
        } else if (group.type === 'gradeGroup') {
          fetchContacts({
            sid: match.params.sid,
            grade: last(split('_', group.value)),
            pageNumber,
            perPage: rowsPerPage,
          });
        } else if (group.type === 'clazzGroup') {
          fetchContactsByClazzGroup({ gid: last(split('_', group.value)), pageNumber, perPage: rowsPerPage });
        } else {
          fetchContactsByCustomGroup({ gid: last(split('_', group.value)), pageNumber, perPage: rowsPerPage });
        }
      } else {
        setPageNumber(1);
      }

      initContactsDeleted();
    }
  }, [
    contactsDeleted.data,
    initContactsDeleted,
    pageNumber,
    group,
    rowsPerPage,
    match.params.sid,
    fetchContacts,
    fetchContactsByClazzGroup,
    fetchContactsByCustomGroup,
  ]);

  useEffect(() => () => {
    dispatch(actions.clearGroupsError());
    dispatch(actions.clearContactsError());
    dispatch(actions.clearContactsDeletedError());
    dispatch(actions.clearFailedContactsDeletedId());
  }, [dispatch]);

  useEffect(() => {
    if (creationTask.error) {
      setErrorMsgSnackbarText('저장에 실패하였습니다.');
      setErrorMsgSnackbarOpen(true);
    }
  }, [creationTask.error]);

  useEffect(() => {
    if (updated.error.groups) {
      setErrorMsgSnackbarText('변경에 실패하였습니다.');
      setErrorMsgSnackbarOpen(true);
    }
  }, [updated.error.groups]);

  return (
    <>
      <Grid container spacing={2} padding={{ top: 24, bottom: 72 }}>
        <Grid
          container
          item
          xs={12}
          alignItems="flex-end"
          margin={{ bottom: 24 }}
        >
          <Heading>
            <FormattedMessage {...messages.contactsHeading} />
          </Heading>
          {contactsCount.data && (
            <S.Count
              value={get('contacts_count', contactsCount.data)}
            >
              {`${get('contacts_count', contactsCount.data)}명`}
            </S.Count>
          )}
        </Grid>
        <Grid container item spacing={2} margin={{ bottom: 4 }}>
          <Grid item xs={3}>
            <SearchField
              placeholder={intl.formatMessage(messages.contactsSearchFieldPlaceholder)}
              value={searchWord}
              onChange={handleSearchWordChange}
              onClear={handleSearchWordClear}
            />
          </Grid>
          <Grid item xs={2}>
            <GroupFilter
              labels={labels}
              open={open.groupFilter}
              options={options}
              value={group.computed}
              onOpen={handleGroupFilterOpen}
              onClose={handleGroupFilterClose}
              onChange={handleGroupChange}
              onSubgroupChange={handleSubgroupChange}
            />
          </Grid>
          <Grid
            container
            item
            justify="flex-end"
            xs={4}
            margin={{ left: 'auto' }}
          >
            <ContactExportButton onClick={handleContactExportOpen} />
            <S.ContactAddLink
              to={{
                pathname: `/schools/${match.params.sid}/contacts/new`,
                state: { from: location.pathname },
              }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
        >
          {/* TODO: 검색 필터 방식 재논의 예정 */}
          {contacts && (
            <ContactTable
              all={all}
              contacts={contacts}
              initUpdateTask={initUpdateTask}
              rowsPerPage={rowsPerPage}
              searchWord={searchWord}
              contactsDeleted={contactsDeleted}
              onOpen={handleContactDetailOpen}
              onFetch={fetchContactsAll}
              onSelect={selectAllContacts}
              onDeselect={deselectAllContacts}
              onDelete={handleDelete}
            />
          )}
        </Grid>
        {!contacts.loading && !isEmpty(get('rows', contacts.data)) && (
          <Grid container item xs={12} justify="space-between" margin={{ top: '2rem' }}>
            <S.CenteredGrid item>
              <Pagination
                pageCount={pageCount}
                pageNumber={pageNumber}
                onClick={handlePageNumberClick}
              />
            </S.CenteredGrid>
            <Grid item>
              <RowsPerPageFilter
                options={rowsPerPageOptions}
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
              />
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          {!contacts.loading && isEmpty(get('rows', contacts.data)) && (
            <TableFallbackMessage>
              <FormattedMessage
                {...messages[searchWord ? 'contactsTableFallbackMessageFiltered' : 'contactsTableFallbackMessageNoData']}
              />
            </TableFallbackMessage>
          )}
        </Grid>
      </Grid>
      <ContactDetailDialog
        contact={contact.data}
        contacts={contacts.data}
        updateTask={updateTask}
        initContact={initContact}
        initUpdateTask={initUpdateTask}
        open={!!open.contactDetail}
        contactEditOpen={open.contactEdit}
        onClose={handleContactDetailClose}
        onContactEditOpen={handleContactEditOpen}
        onEnter={handleContactDetailEnter}
        onFetch={fetchContact}
        onSubmit={exchangeGuardianContacts}
      />
      <GroupSettingsDialog
        groups={groups.data}
        open={open.groupSettings}
        onUpdate={applyChangedGroups}
        onGroupAddOpen={handleGroupAddOpen}
        onClose={handleGroupSettingsClose}
        deleteGroupCheckOpen={open.deleteGroupCheck}
        onDeleteGroupCheckOpen={handleDeleteGroupCheckOpen}
        onDeleteGroupCheckClose={handleDeleteGroupCheckClose}
      />
      <GroupAddDialog
        open={open.groupAdd}
        creationTask={creationTask}
        createClazzGroup={createClazzGroup}
        createCustomGroup={createCustomGroup}
        initCreationTask={initCreationTask}
        onClose={handleGroupAddClose}
      />
      <ContactEditDialog
        contact={contact.data}
        contactDetailOpen={!!open.contactDetail}
        updateTask={updateTask}
        groups={groups.data}
        initContact={initContact}
        initUpdateTask={initUpdateTask}
        open={open.contactEdit}
        onClose={handleContactEditClose}
        onSubmit={updateContact}
        openContactDetailDialog={handleContactDetailOpen}
      />
      <ContactExportDialog
        fileName={fileName}
        open={open.contactExport}
        onClose={handleContactExportClose}
        onFetch={fetchContactsAll}
      />
      {(contact.loading
        || allContacts.loading
        || creationTask.loading
        || updateTask.loading
        || updated.loading.groups
        || contactsDeleted.loading) && <Spinner />}

      <Dialog
        open={errorDialogOpen}
        onClose={handleCloseErrorDialog}
      >
        <S.ErrorDialogContent id="alert-dialog-title">{errorDialogText}</S.ErrorDialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleCloseErrorDialog}
          >
            닫기
          </Button>
          <S.RetryButton
            onClick={handleRetry}
            variant="outlined"
          >
            재시도
          </S.RetryButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={errorSnackbarOpen}
        message={errorSnackbarText}
        onClose={handleCloseErrorSnackbar}
        autoHideDuration={5000}
        action={visibleSnackbarRetryButton && (
          <S.SnackbarRetryButton
            size="small"
            onClick={handleSnackbarRetry}
          >
            재시도
          </S.SnackbarRetryButton>
        )}
      />
      <Snackbar
        autoHideDuration={3500}
        open={errorMsgSnackbarOpen}
        onClose={handleCloseErrorMsgSnackbar}
        onExited={handleErrorMsgSnackbarExited}
        message={errorMsgSnackbarText}
        TransitionComponent={Fade}
      />
    </>
  );
};

Contacts.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default injectIntl(Contacts);
